<script>
import V4Button from 'dashboard/components-next/button/Button.vue';
import { useAlert, useTrack } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import ReportFilterSelector from './components/FilterSelector.vue';
import { GROUP_BY_FILTER } from './constants';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import ReportContainer from './ReportContainer.vue';
import ReportHeader from './components/ReportHeader.vue';

const REPORTS_KEYS = {
  CONVERSATIONS: 'conversations_count',
  INCOMING_MESSAGES: 'incoming_messages_count',
  OUTGOING_MESSAGES: 'outgoing_messages_count',
  FIRST_RESPONSE_TIME: 'avg_first_response_time',
  RESOLUTION_TIME: 'avg_resolution_time',
  RESOLUTION_COUNT: 'resolutions_count',
  REPLY_TIME: 'reply_time',
};

export default {
  name: 'ConversationReports',
  components: {
    ReportHeader,
    ReportFilterSelector,
    ReportContainer,
    V4Button,
  },
  data() {
    return {
      from: 0,
      to: 0,
      groupBy: GROUP_BY_FILTER[1],
      businessHours: false,
    };
  },
  created() {
    this.fetchAllData = debounce(
      this.fetchAllData,
      1000,
      false
    );
  },
  methods: {
    fetchAllData() {
      this.fetchAccountSummary();
      this.fetchChartData();
    },
    fetchAccountSummary() {
      try {
        this.$store.dispatch('fetchAccountSummary', this.getRequestPayload());
      } catch {
        useAlert(this.$t('REPORT.SUMMARY_FETCHING_FAILED'));
      }
    },
    fetchChartData() {
      [
        'CONVERSATIONS',
        'INCOMING_MESSAGES',
        'OUTGOING_MESSAGES',
        'FIRST_RESPONSE_TIME',
        'RESOLUTION_TIME',
        'RESOLUTION_COUNT',
        'REPLY_TIME',
      ].forEach(async key => {
        try {
          await this.$store.dispatch('fetchAccountReport', {
            metric: REPORTS_KEYS[key],
            ...this.getRequestPayload(),
          });
        } catch {
          useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
        }
      });
    },
    getRequestPayload() {
      const { from, to, groupBy, businessHours, inbox } = this;
      return {
        from,
        to,
        groupBy: groupBy?.period,
        businessHours,
        inbox: inbox?.map(item => item.id),
      };
    },
    downloadAgentReports() {
      const { from, to, inbox } = this;
      const fileName = `agent-report-${format(
        fromUnixTime(to),
        'dd-MM-yyyy',
      )}.csv`;
      this.$store.dispatch('downloadAgentReports', {
        from,
        to,
        fileName,
        inbox: inbox?.map(item => item.id),
      });
    },
    downloadAgentDailyReports() {
      const { from, to, inbox } = this;
      const fileName = `agent-daily-report-${format(
        fromUnixTime(to),
        'dd-MM-yyyy',
      )}.csv`;
      this.$store.dispatch('downloadAgentDailyReports', {
        from,
        to,
        fileName,
        inbox: inbox?.map(item => item.id),
      });
    },
    onFilterChange({ from, to, groupBy, businessHours, selectedInbox }) {
      this.from = from;
      this.to = to;
      this.groupBy = groupBy;
      this.businessHours = businessHours;
      this.inbox = selectedInbox;
      this.fetchAllData();

      useTrack(REPORTS_EVENTS.FILTER_REPORT, {
        filterValue: { from, to, groupBy, businessHours, selectedInbox },
        reportType: 'conversations',
      });
    },
  },
};
</script>

<template>
  <ReportHeader :header-title="$t('REPORT.HEADER')">
    <div>
      <V4Button
        :style="{ marginRight: '10px' }"
        :label="'Download Daily Report'"
        icon="i-ph-download-simple"
        size="sm"
        @click="downloadAgentDailyReports"
      />
      <V4Button
        :label="$t('REPORT.DOWNLOAD_AGENT_REPORTS')"
        icon="i-ph-download-simple"
        size="sm"
        @click="downloadAgentReports"
      />
    </div>
  </ReportHeader>
  <div class="flex flex-col gap-3">
    <ReportFilterSelector
      :show-agents-filter="false"
      show-group-by-filter
      show-inbox-filter
      @filter-change="onFilterChange"
    />
    <ReportContainer :group-by="groupBy" />
  </div>
</template>
